/**
 * GoogleSignInButton.jsx
 * Wraps Google Identity Services to render the official "Sign in with Google"
 * button. On success, hands the returned ID token to the onSuccess callback.
 *
 * Requires:
 *   1. The GSI script tag in public/index.html:
 *      <script src="https://accounts.google.com/gsi/client" async defer></script>
 *   2. REACT_APP_GOOGLE_CLIENT_ID in the frontend's .env file.
 *
 * Props:
 *   onSuccess   (function)  Called with the raw ID token on success
 *   onError     (function)  Called with a string message on failure (optional)
 *   width       (number)    Button width in pixels (default 320)
 *   text        (string)    "signin_with" | "signup_with" | "continue_with"  (default "continue_with")
 *
 * Team: TA37 - The Coders' Assembly
 */

import React, { useEffect, useRef, useState } from 'react';
import './GoogleSignInButton.css';

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const GoogleSignInButton = ({
  onSuccess,
  onError,
  width = 320,
  text = 'continue_with',
}) => {
  const buttonRef = useRef(null);
  const [gsiReady, setGsiReady] = useState(false);

  // Poll briefly for window.google to appear. The GSI script is loaded async
  // from index.html so it may or may not be ready when this component mounts.
  useEffect(() => {
    if (window.google?.accounts?.id) {
      setGsiReady(true);
      return undefined;
    }

    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      if (window.google?.accounts?.id) {
        setGsiReady(true);
        clearInterval(interval);
      } else if (attempts > 40) {
        // ~4 seconds
        clearInterval(interval);
        if (onError) onError('Google sign-in failed to load. Please refresh.');
      }
    }, 100);

    return () => clearInterval(interval);
  }, [onError]);

  // Render the button once GSI is ready.
  useEffect(() => {
    if (!gsiReady || !buttonRef.current) return;
    if (!CLIENT_ID) {
      if (onError) {
        onError(
          'Google sign-in is not configured. REACT_APP_GOOGLE_CLIENT_ID is missing.'
        );
      }
      return;
    }

    try {
      window.google.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: (response) => {
          if (response?.credential) {
            onSuccess(response.credential);
          } else if (onError) {
            onError('Sign-in was cancelled or failed.');
          }
        },
        // Don't show the one-tap auto-prompt; we want an explicit click.
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      window.google.accounts.id.renderButton(buttonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text,
        shape: 'rectangular',
        logo_alignment: 'left',
        width,
      });
    } catch (err) {
      console.error('Failed to render Google Sign-In button:', err);
      if (onError) onError('Could not render the Google sign-in button.');
    }
  }, [gsiReady, onSuccess, onError, width, text]);

  if (!CLIENT_ID) {
    return (
      <p className="google-signin-button__error">
        Google sign-in is unavailable. Ask an administrator to configure
        REACT_APP_GOOGLE_CLIENT_ID.
      </p>
    );
  }

  return (
    <div
      className="google-signin-button"
      ref={buttonRef}
      aria-label="Sign in with Google"
    />
  );
};

export default GoogleSignInButton;
